import React, { useState, useEffect } from "react";
import {
    TextField,
    Button,
    MenuItem,
    Chip,
    Select,
    InputLabel,
    FormControl,
    Box,
    Grid,
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    Typography,
    InputAdornment,
    useTheme,
} from "@mui/material";

import AdminApi from "Service/Admin.api";
import { StyledTableCell } from "../AdminList/ProfessionalUserList";
import ColorData from "Components/CstmTheme/ColorData";
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from '@mui/icons-material/Settings';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import { setAlertData, startLoading, stopLoading } from '../../Redux/Screen/Action';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import Pagination from '@mui/material/Pagination';
import { RouteUrls } from "Constants/RouteUrls";


interface CaseItem {
    pkCase: number;
    caseNumber: string;
    caseName?: string;
    description: string;
    casePrice: number;
    client: {
        firstName: string;
        lastName: string;
        email: string;
        contactNumber: string;
    };
    currentProcessingStatus?: {
        pkProcessingStatus: number;
        statusName: string;
    } | null;
    assignedProfessional?: {
        firstName: string;
        lastName: string;
        contactNumber: string;
        id: number;
    } | null;
    assignedAdmin?: any | null;
    caseType: {
        caseTypeName: string;
        pkCaseType: number;
    };
    orderType: {
        orderTypeName: string;
        orderPrice: number;
    };
    casePaymentStatus?: string | number;
    verified?: boolean;
    isDeleted?: boolean;
}

const CaseManagementHub: React.FC = () => {
    const navigate = useNavigate();
    const { myInfo } = useSelector((state: any) => state?.user);
    const [caseList, setCaseList] = useState<CaseItem[]>([]);
    const [verifyProfessionalList, setVerifyProfessionalList] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const theme = useTheme();
    const dispatch = useDispatch();
    const [selectedProId, setSelectedProId] = useState("");

    useEffect(() => {
        const initLoad = async () => {
            await fetchVerifyProfessionalList();
        };
        initLoad();
    }, []);

    useEffect(() => {
        loadCases();
    }, [page, pageSize, searchQuery, selectedProId]);

    const loadCases = async () => {
        try {
            dispatch(startLoading({ loading: true, loadtext: "Fetching cases..." }));
            const res = await AdminApi.getAllCasesWithAssignedAdminProfessional(page, pageSize, searchQuery, selectedProId ? Number(selectedProId) : undefined);

            // // Filter out 'Admin Support' cases
            // const filteredCases = (res.data.data || []).filter(
            //     (c: CaseItem) => c.orderType?.orderTypeName?.trim().toLowerCase() !== "admin support"
            // );

            const filteredCases = res.data.data || [];

            setCaseList(filteredCases);
            // Optional: Adjust total count if you rely on frontend filtering, though true pagination total count requires backend adjustment.
            setTotalCount(res.data.totalCount);
        } catch (error) {
            console.error("Error fetching cases:", error);
        } finally {
            dispatch(stopLoading());
        }
    };

    const fetchVerifyProfessionalList = async () => {
        try {
            const res = await AdminApi.GetAllActiveProfessionalUser();
            setVerifyProfessionalList(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error("Error fetching professional users:", error);
        }
    };

    const handleManageCase = (caseId: number) => {
        navigate(`/${RouteUrls.CaseControlCenter}/${caseId}`);
    };

    const handleDeleteCase = async (caseId: number) => {
        if (window.confirm("Are you sure you want to delete this case? This action cannot be undone.")) {
            try {
                dispatch(startLoading({ loading: true, loadtext: "Deleting case..." }));
                const res = await AdminApi.deleteCase(caseId);
                if (res?.success || res?.data?.success) {
                    dispatch(setAlertData({ openAlert: true, alertMessage: "Case deleted successfully.", alertVariant: "success" }));
                    loadCases();
                } else {
                    dispatch(setAlertData({ openAlert: true, alertMessage: res?.message || res?.data?.message || "Failed to delete case.", alertVariant: "error" }));
                }
            } catch (error: any) {
                console.error("Error deleting case:", error);
                const errorMessage = error?.response?.data?.message || "Error deleting case.";
                dispatch(setAlertData({ openAlert: true, alertMessage: errorMessage, alertVariant: "error" }));
            } finally {
                dispatch(stopLoading());
            }
        }
    };

    return (
        <Box sx={{
            py: 4,
            pb: 12,
            display: "flex",
            flexDirection: "column",
            height: "100vh",
            overflowY: "auto",
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold" sx={{
                    padding: 2,
                    borderRadius: 2,
                    backgroundColor: theme => ColorData.primary.main,
                    color: theme => ColorData.text.secondary,
                    width: 'fit-content'
                }}>
                    Case Management Hub
                </Typography>

                {myInfo?.userType === "SUPERADMIN" && (
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<LibraryBooksIcon />}
                        onClick={() => navigate(`/${RouteUrls.BulkAssignNeutral}`)}
                        sx={{ fontWeight: 'bold' }}
                    >
                        Bulk Assign ODR Cases
                    </Button>
                )}
            </Box>

            {/* Search and Actions */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                        fullWidth
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setPage(1);
                        }}
                        placeholder="Search Cases..."
                        variant="outlined"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="primary" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                                '&:hover fieldset': {
                                    borderColor: theme.palette.primary.main,
                                    color: 'white',
                                },
                            },
                        }}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <Box display="flex" gap={1} height="100%" alignItems="center">
                        <Chip
                            icon={<PersonIcon />}
                            label={`Total Cases: ${totalCount}`}
                            color="info"
                            variant="outlined"
                            sx={{
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                height: { xs: 28, sm: 32 }
                            }}
                        />
                    </Box>
                </Grid>

                {/* Professional Filter Dropdown */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel id="pro-filter-label">Filter by Professional</InputLabel>
                        <Select
                            labelId="pro-filter-label"
                            value={selectedProId}
                            label="Filter by Professional"
                            onChange={(e) => {
                                setSelectedProId(e.target.value);
                                setPage(1);
                            }}
                            sx={{ borderRadius: 3, bgcolor: 'white' }}
                        >
                            <MenuItem value="">
                                <em>All Professionals</em>
                            </MenuItem>
                            {verifyProfessionalList?.map((pro: any) => (
                                <MenuItem key={pro.id} value={pro.id}>
                                    {pro.firstName} {pro.lastName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            {/* LISTING TABLE */}
            <Paper sx={{ px: 2, py: 2, boxShadow: 'none' }}>
                <TableContainer
                    sx={{
                        maxHeight: 550,
                        overflowY: "auto",
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                    }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Case Id</StyledTableCell>
                                <StyledTableCell>Case Name</StyledTableCell>
                                <StyledTableCell>Case & Order Type</StyledTableCell>
                                <StyledTableCell>Client Details</StyledTableCell>
                                <StyledTableCell>Assigned Prof.</StyledTableCell>
                                <StyledTableCell>Assigned Admin</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell align="center">Action</StyledTableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {caseList?.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        No Cases Found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                caseList?.map((caseItem) => (
                                    <TableRow key={caseItem.pkCase} hover>
                                        <TableCell>{caseItem.caseNumber ? caseItem.caseNumber : caseItem.pkCase}</TableCell>
                                        <TableCell>{caseItem.caseName}</TableCell>

                                        <TableCell>
                                            <div style={{ fontWeight: 'bold' }}>{caseItem.caseType?.caseTypeName}</div>
                                            <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                                Order: {caseItem.orderType?.orderTypeName || 'N/A'}
                                            </div>
                                        </TableCell>

                                        <TableCell>
                                            <div>{`${caseItem.client?.firstName} ${caseItem.client?.lastName}`}</div>
                                            <div style={{ fontSize: '0.75rem', color: '#1976d2' }}>{caseItem.client?.email}</div>
                                            <div style={{ fontSize: '0.75rem' }}>{caseItem.client?.contactNumber}</div>
                                        </TableCell>

                                        <TableCell>
                                            {caseItem.assignedProfessional ? (
                                                <>
                                                    <div>{`${caseItem.assignedProfessional.firstName} ${caseItem.assignedProfessional.lastName}`}</div>
                                                    <div style={{ fontSize: '0.7rem', color: '#666' }}>{caseItem.assignedProfessional.contactNumber}</div>
                                                </>
                                            ) : (
                                                <Chip label="Unassigned" size="small" variant="outlined" />
                                            )}
                                        </TableCell>

                                        <TableCell>
                                            {caseItem.assignedAdmin ? (
                                                <div>{caseItem.assignedAdmin.firstName}</div>
                                            ) : (
                                                <span style={{ color: '#ccc', fontStyle: 'italic' }}>Pending</span>
                                            )}
                                        </TableCell>

                                        <TableCell>
                                            <Chip
                                                label={caseItem.currentProcessingStatus?.statusName || "Unknown"}
                                                size="small"
                                            />
                                        </TableCell>

                                        <TableCell align="center">
                                            <Box display="flex" justifyContent="center" gap={1}>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    color="primary"
                                                    startIcon={<SettingsIcon sx={{ width: 14, height: 14 }} />}
                                                    onClick={() => handleManageCase(caseItem.pkCase)}
                                                    sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                                                >
                                                    Manage Case
                                                </Button>
                                                {caseItem.isDeleted ? (
                                                    <Chip label="Deleted" color="error" size="small" variant="filled" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }} />
                                                ) : (
                                                    myInfo?.userType === "SUPERADMIN" && (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleDeleteCase(caseItem.pkCase)}
                                                            sx={{ fontSize: '0.7rem', textTransform: 'none', minWidth: 'auto', p: '4px 8px' }}
                                                        >
                                                            Delete
                                                        </Button>
                                                    )
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ mr: 2, color: 'text.secondary' }}>
                        Total: {totalCount} items
                    </Typography>
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 100, mr: 3 }}>
                        <Select
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setPage(1);
                            }}
                        >
                            <MenuItem value={10}>10 / page</MenuItem>
                            <MenuItem value={20}>20 / page</MenuItem>
                            <MenuItem value={50}>50 / page</MenuItem>
                            <MenuItem value={100}>100 / page</MenuItem>
                        </Select>
                    </FormControl>
                    <Pagination
                        count={Math.ceil(totalCount / pageSize)}
                        page={page}
                        onChange={(_e, value) => setPage(value)}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            </Paper>
        </Box>
    );
};

export default CaseManagementHub;
